import { useEffect, useMemo, useRef, useState } from "react";
import { Send, X } from "lucide-react";
import { chatFileMaxBytes, chatUploadTooLargeMessage, isChatVideoFile } from "@/lib/chatApi";

type Props = {
  file: File;
  onCancel: () => void;
  onSend: (file: File, caption: string) => void | Promise<boolean | void>;
};

export function ChatVideoComposeSheet({ file, onCancel, onSend }: Props) {
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    return () => URL.revokeObjectURL(url);
  }, [url]);

  useEffect(() => {
    setCaption("");
    setBusy(false);
    setErr(null);
  }, [file]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  const send = async () => {
    if (busy) return;
    if (!isChatVideoFile(file) && !file.type.startsWith("video/")) {
      setErr("Ce fichier n’est pas une vidéo.");
      return;
    }
    if (file.size > chatFileMaxBytes(file)) {
      setErr(chatUploadTooLargeMessage(file.name, "50 Mo"));
      return;
    }
    setBusy(true);
    setErr(null);
    const ok = await onSend(file, caption.trim());
    if (ok === false) setBusy(false);
  };

  return (
    <div
      className="chat-photo-filter chat-photo-filter--video"
      role="dialog"
      aria-modal="true"
      aria-labelledby="chat-video-compose-title"
    >
      <h2 id="chat-video-compose-title" className="sr-only">
        Vidéo
      </h2>
      <button
        type="button"
        className="chat-photo-filter__close"
        disabled={busy}
        aria-label="Annuler"
        onClick={onCancel}
      >
        <X className="size-5" />
      </button>

      <div className="chat-photo-filter__stage">
        <video
          src={url}
          className="chat-photo-filter__preview chat-photo-filter__preview--video"
          controls
          playsInline
          preload="metadata"
        />
      </div>

      <div className="chat-photo-filter__dock">
        {err ? <p className="chat-photo-filter__err">{err}</p> : null}
        <div className="chat-photo-filter__compose">
          <textarea
            ref={captionRef}
            value={caption}
            rows={1}
            disabled={busy}
            placeholder="Légende…"
            className="chat-photo-filter__caption"
            onChange={(e) => setCaption(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <button
            type="button"
            className="chat-photo-filter__send"
            disabled={busy}
            aria-label="Envoyer"
            title="Envoyer"
            onClick={() => void send()}
          >
            <Send className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
